import { createContext, useContext, useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useSearchParams as useReactRouterSearchParams } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { NotificationProvider } from "@/hooks/useNotifications";
import { BrandingProvider } from "@/hooks/useBranding";

// Pages
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import MarketSelection from "@/pages/MarketSelection";
import Dashboard from "@/pages/Dashboard";
import TicketList from "@/pages/TicketList";
import TicketDetail from "@/pages/TicketDetail";
import NewTicket from "@/pages/NewTicket";
import AdminPanel from "@/pages/AdminPanel";
import PendingApproval from "@/pages/PendingApproval";
import VendorManagement from "@/pages/VendorManagement";
import AuditLogs from "@/pages/AuditLogs";
import HelpDeskQueue from "@/pages/HelpDeskQueue";
import TechnicianView from "@/pages/TechnicianView";
import Analytics from "@/pages/Analytics";
import TreasurerPaymentConfirm from "@/pages/TreasurerPaymentConfirm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// Unit Context
const UnitContext = createContext(null);

export const useUnit = () => {
  const context = useContext(UnitContext);
  if (!context) throw new Error("useUnit must be used within UnitProvider");
  return context;
};

// API instance with auth header
export const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("efacilicare_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("efacilicare_token");
      localStorage.removeItem("efacilicare_user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Auth Provider Component
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  useEffect(() => {
    const storedToken = localStorage.getItem("efacilicare_token");
    const savedUser = localStorage.getItem("efacilicare_user");
    
    if (storedToken && savedUser) {
      setToken(storedToken);
      setUser(JSON.parse(savedUser));
      api.get("/auth/me")
        .then((res) => {
          setUser(res.data);
          localStorage.setItem("efacilicare_user", JSON.stringify(res.data));
        })
        .catch(() => {
          localStorage.removeItem("efacilicare_token");
          localStorage.removeItem("efacilicare_user");
          setUser(null);
          setToken(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    const { access_token, user: userData } = res.data;
    localStorage.setItem("efacilicare_token", access_token);
    localStorage.setItem("efacilicare_user", JSON.stringify(userData));
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("efacilicare_token");
    localStorage.removeItem("efacilicare_user");
    localStorage.removeItem("efacilicare_unit");
    setUser(null);
    setToken(null);
  };

  const register = async (data) => {
    await api.post("/auth/register", data);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, register, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

// Unit Provider Component
function UnitProvider({ children }) {
  const [currentUnit, setCurrentUnitState] = useState(null);

  useEffect(() => {
    const saved = localStorage.getItem("efacilicare_unit");
    if (saved) {
      setCurrentUnitState(JSON.parse(saved));
    }
  }, []);

  const setCurrentUnit = useCallback((unit) => {
    if (unit) {
      localStorage.setItem("efacilicare_unit", JSON.stringify(unit));
    } else {
      localStorage.removeItem("efacilicare_unit");
    }
    setCurrentUnitState(unit);
  }, []);

  return (
    <UnitContext.Provider value={{ currentUnit, setCurrentUnit }}>
      {children}
    </UnitContext.Provider>
  );
}

// Protected Route Component
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user.status === "pending") {
    return <Navigate to="/pending" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role) && user.role !== "SystemAdmin") {
    return <Navigate to="/select-market" replace />;
  }

  return children;
}

// Pending Approval Page
function PendingApprovalPage() {
  const { logout } = useAuth();
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center animate-slide-up">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Account Pending Approval</h2>
        <p className="text-gray-600 mb-6">
          Your account is pending approval. An administrator will assign your role and business unit shortly. 
          You will receive an email notification once your account is activated.
        </p>
        <button
          onClick={logout}
          className="text-indigo-600 hover:text-indigo-700 font-medium"
        >
          Sign Out
        </button>
      </div>
    </div>
  );
}

function AppRoutes() {
  const { user, token, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <NotificationProvider token={token} userId={user?.id}>
      <BrandingProvider isAuthenticated={!!user}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginRouteGuard user={user} />} />
        <Route path="/register" element={user ? <Navigate to="/select-market" /> : <RegisterPage />} />
        <Route path="/pending" element={<PendingApprovalPage />} />
        <Route path="/treasurer/payment-confirm" element={<TreasurerPaymentConfirm />} />
        
        {/* Protected routes */}
        <Route
          path="/select-market"
          element={
            <ProtectedRoute>
              <MarketSelection />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/:unitId"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tickets/:unitId"
          element={
            <ProtectedRoute>
              <TicketList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ticket/:ticketId"
          element={
            <ProtectedRoute>
              <TicketDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/new-ticket/:unitId"
          element={
            <ProtectedRoute>
              <NewTicket />
            </ProtectedRoute>
          }
        />
        <Route
          path="/helpdesk/:unitId"
          element={
            <ProtectedRoute allowedRoles={["HelpDesk"]}>
              <HelpDeskQueue />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-tickets/:unitId"
          element={
            <ProtectedRoute allowedRoles={["Technician", "Vendor"]}>
              <TechnicianView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/approvals/:unitId"
          element={
            <ProtectedRoute allowedRoles={["District Manager", "District Operations", "Finance", "President"]}>
              <PendingApproval />
            </ProtectedRoute>
          }
        />
        <Route
          path="/vendors/:unitId"
          element={
            <ProtectedRoute allowedRoles={["HelpDesk"]}>
              <VendorManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics/:unitId"
          element={
            <ProtectedRoute allowedRoles={["SystemAdmin", "Finance", "District Manager", "District Operations", "President"]}>
              <Analytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={["SystemAdmin"]}>
              <AdminPanel />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/audit-logs"
          element={
            <ProtectedRoute allowedRoles={["SystemAdmin"]}>
              <AuditLogs />
            </ProtectedRoute>
          }
        />
        
        {/* Default redirect */}
        <Route path="/" element={<Navigate to={user ? "/select-market" : "/login"} />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      </BrandingProvider>
    </NotificationProvider>
  );
}

function PwaInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Only show if not dismissed before
      if (!localStorage.getItem('pwa-dismissed')) setShow(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  if (!show) return null;

  return (
    <div className="fixed bottom-20 right-4 z-50 bg-white border border-gray-200 shadow-lg rounded-xl p-4 max-w-xs" data-testid="pwa-install-banner">
      <p className="text-sm font-medium text-gray-900 mb-1">Install Efacilicare</p>
      <p className="text-xs text-gray-500 mb-3">Add to your home screen for quick access</p>
      <div className="flex gap-2">
        <button
          onClick={async () => {
            if (deferredPrompt) {
              deferredPrompt.prompt();
              await deferredPrompt.userChoice;
            }
            setShow(false);
          }}
          className="flex-1 bg-indigo-600 text-white text-xs py-1.5 rounded-lg hover:bg-indigo-700"
          data-testid="pwa-install-btn"
        >
          Install
        </button>
        <button
          onClick={() => { setShow(false); localStorage.setItem('pwa-dismissed', '1'); }}
          className="flex-1 bg-gray-100 text-gray-600 text-xs py-1.5 rounded-lg hover:bg-gray-200"
        >
          Not now
        </button>
      </div>
    </div>
  );
}

function LoginRouteGuard({ user }) {
  const [params] = useReactRouterSearchParams();
  const redirect = params.get("redirect");
  if (user) {
    return <Navigate to={redirect || "/select-market"} replace />;
  }
  return <LoginPage />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <UnitProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors />
            <PwaInstallBanner />
          </UnitProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
